import React, { ReactNode, useEffect } from 'react';
import { Form, Button, Table } from 'react-bootstrap';
import { Link, useForm, usePage } from '@inertiajs/react';

import AuthedLayout from '../../Layouts/Authed';
import Valuation from '../../Data/Valuation';
import NonRecurringRevenue from '../../Data/NonRecurringRevenue';
import NonRecurringRevenueRow from './NonRecurringRevenue/NonRecurringRevenueRow';
import { getEventValue, replaceNullWithEmpty } from './Helpers/RepeatingForms';
import FormHeader from '../../Components/Forms/FormHeader';
import InstructionsPanel from '../../Components/Forms/InstructionsPanel';
import formatCurrency from './Helpers/FormatCurrency';
import AutosaveStatus from '../../Components/Forms/AutosaveStatus';
import useValuationStepAutosave from './Helpers/useValuationStepAutosave';
import MarkComplete from '../../Components/Forms/MarkComplete';

interface Props {
	valuation: Valuation;
	non_recurring_revenue: NonRecurringRevenue[];
}

interface FormData {
	non_recurring_revenue: Partial<NonRecurringRevenue>[];
	mark_complete: boolean;
}

const NonRecurringRevenues = (props: Props) => {
	const { errors, readOnly } = usePage<any>().props;

	const { data, setData, post } = useForm<FormData>({
		non_recurring_revenue:
			replaceNullWithEmpty(props.non_recurring_revenue) ?? [],
		mark_complete: props.valuation.completed_steps?.includes('non-recurring-revenues') ?? false,
	});
	const autosaveStatus = useValuationStepAutosave('non-recurring-revenues', props.valuation.id, data);

	useEffect(() => {
		if (data.non_recurring_revenue.length == 0) {
			addRow();
		}
	}, [props.non_recurring_revenue]);

	const submit = (event: any) => {
		event.preventDefault();
		post(`/valuation-form/${props.valuation.id}/non-recurring-revenues`);
	};

	const addRow = () => {
		setData({
			...data,
			non_recurring_revenue: [
				...data.non_recurring_revenue,
				{
					id: '',
					account_name: '',
					industry: '',
					total_agency_commissions_booked: '',
					num_years_with_agency: '',
					agency_producer: '',
				},
			] as NonRecurringRevenue[],
		});
	};

	const updateIndex = (event: React.ChangeEvent<any>, index: number) => {
		const newData: any = { ...data };
		newData.non_recurring_revenue[index][event.target.name] =
			getEventValue(event);
		setData(newData);
	};

	const deleteIndex = (index: number) => {
		const newData: any = { ...data };
		newData.non_recurring_revenue.splice(index, 1);
		setData(newData);
	};

	const totalCommissionsLost = formatCurrency(
		data.non_recurring_revenue.reduce(
			(accumulator, currentValue) =>
				accumulator + (currentValue?.total_agency_commissions_booked || 0),
			0
		)
	);

	const avgYearsWithAgency = data.non_recurring_revenue.reduce(
		(accumulator, currentValue) =>
			accumulator + (currentValue?.num_years_with_agency || 0),
		0
	) / data.non_recurring_revenue.length;

	useEffect(() => {
		scrollTo(0,0);
	}, []);

	return (
		<div>
			<FormHeader
				agencyName={props.valuation.agency_name}
				effectiveDate={props.valuation.effective_date}
				pageName='Non-recurring Revenues'
			/>
			<InstructionsPanel>
				Use this tab to record any policies or clients you've lost in the last year where their renewal commissions are not expected in the year to come.
			</InstructionsPanel>
			<Form onSubmit={submit} className='valuationForm'>
				<fieldset disabled={readOnly}>
				<Table striped borderless hover responsive>
					<thead>
						<tr>
							<th
								id='non_recurring_revenueNum'
								className='cell-xs sticky-cell'
							>
								#
							</th>
							<th id='account_name'>Account Name</th>
							<th id='industry'>Industry</th>
							<th
								id='total_agency_commissions_booked'
								className='cell-lg'
							>
								Total Agency Commissions Booked for TTM
							</th>
							<th id='num_years_with_agency'>
								# Years with Agency
							</th>
							<th id='agency_producer'>Agency Producer</th>
							<th id='delete_element' className='cell-sm'></th>
						</tr>
					</thead>
					<tbody>
						{data.non_recurring_revenue.map(
							(
								non_recurring_revenue: Partial<NonRecurringRevenue>,
								index: number
							) => (
								<NonRecurringRevenueRow
									key={`non_recurring_revenue-${index}`}
									non_recurring_revenue={
										non_recurring_revenue
									}
									index={index}
									errors={errors}
									updateIndex={updateIndex}
									deleteIndex={deleteIndex}
								/>
							)
						)}
					</tbody>
					<tfoot>
						<tr>
							<th />
							<th />
							<th />
							<th>{totalCommissionsLost}</th>
							<th>Avg: {avgYearsWithAgency}</th>
							<th />
							<th />
						</tr>
					</tfoot>
				</Table>
				</fieldset>
				<div className='d-flex justify-content-between align-items-center'>
					<Button
						as={Link as any}
						href={`/valuation-form/${props.valuation.id}/producer-bobs`}
						variant='outline-primary'
						className='mt-4'
					>
						Back
					</Button>
					<div className='d-flex align-items-center'>
						{!readOnly && <AutosaveStatus status={autosaveStatus} />}
						{!readOnly && (
							<MarkComplete
								checked={data.mark_complete}
								onChange={(checked) => setData('mark_complete', checked)}
								className='mt-4 ms-4'
							/>
						)}
						{!readOnly && (
							<Button onClick={addRow} className='mt-4 ms-4'>
								Add Row
							</Button>
						)}
						{readOnly ? (
							<Button
								as={Link as any}
								href={`/valuation-form/${props.valuation.id}/non-recurring-expenses`}
								className='mt-4 ms-4'
							>
								Next
							</Button>
						) : (
							<Button type='submit' className='mt-4 ms-4'>
								Next
							</Button>
						)}
					</div>
				</div>
			</Form>
		</div>
	);
};

NonRecurringRevenues.layout = (page: ReactNode) => (
	<AuthedLayout>{page}</AuthedLayout>
);

export default NonRecurringRevenues;
